import { useState, useMemo } from "react";
import { Monitor, Moon, Sun } from "lucide-react";
import { useTranslation } from "react-i18next";
import { FormContent } from "@/components/page-canvas";

import { Button } from "@/components/ui/button";
import { FieldGroup } from "@/components/ui/field";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { useAppearance, type Appearance } from "@/hooks/use-appearance";

const appearanceOptions: {
  value: Appearance;
  labelKey: string;
  icon: React.ElementType;
}[] = [
  { value: "light", labelKey: "profile_ui.light", icon: Sun },
  { value: "dark", labelKey: "profile_ui.dark", icon: Moon },
  { value: "system", labelKey: "profile_ui.system", icon: Monitor },
];

export function AppearanceForm() {
  const { t } = useTranslation();
  const { appearance, updateAppearance } = useAppearance();
  const [selection, setSelectedAppearance] = useState<Appearance | null>(null);
  const selectedAppearance = selection ?? appearance;

  const isDirty = useMemo(() => {
    return selectedAppearance !== appearance;
  }, [selectedAppearance, appearance]);

  const handleSave = () => {
    updateAppearance(selectedAppearance);
  };

  return (
    <FormContent>
      <FieldGroup>
        <div className="@container/appearance">
          <RadioGroup
            value={selectedAppearance}
            onValueChange={(value) =>
              setSelectedAppearance(value as Appearance)
            }
            className="grid grid-cols-1 gap-4 @min-[576px]/appearance:grid-cols-3"
          >
            {appearanceOptions.map((option) => {
              const Icon = option.icon;
              return (
                <Label
                  key={option.value}
                  htmlFor={option.value}
                  className="cursor-pointer"
                >
                  <div
                    className={`flex flex-col items-center gap-2 rounded-lg border-2 p-4 transition-colors hover:bg-accent ${
                      selectedAppearance === option.value
                        ? "border-primary bg-accent"
                        : "border-transparent"
                    }`}
                  >
                    <Icon className="h-6 w-6" />
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value={option.value} id={option.value} />
                      <span className="text-sm font-medium">
                        {t(option.labelKey)}
                      </span>
                    </div>
                  </div>
                </Label>
              );
            })}
          </RadioGroup>
        </div>

        <div className="flex justify-start">
          <Button type="button" onClick={handleSave} disabled={!isDirty}>
            {t("profile_ui.save_appearance")}
          </Button>
        </div>
      </FieldGroup>
    </FormContent>
  );
}
